import { useTranslation } from "react-i18next";
import { Card, Divider, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import CopyButton from "../CopyButton";

export type ContactSupportProps = {
	supportEmail: string;
	subject: string;
	body: string;
	appName: string;
	platform: string;
	version?: string;
};

export const ContactSupport = ({
	supportEmail,
	subject,
	body,
	appName,
	platform,
	version,
}: ContactSupportProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const copyText = `${supportEmail}\n\n${subject}\n\n${body}`;

	return (
		<Card mode="elevated" style={{ overflow: "hidden" }}>
			<Card.Title
				title={
					<Text variant="titleLarge">
						{t("redeemCopilot.contactSupport.title")}
					</Text>
				}
				right={() => <CopyButton text={copyText} size={20} />}
			/>
			<Divider style={{ backgroundColor: theme.colors.surfaceVariant }} />

			<Card.Content>
				<Box style={{ paddingVertical: 16 }}>
					<Box style={{ flexDirection: "column", gap: 12 }}>
						<Box style={{ flexDirection: "column", gap: 2 }}>
							<Text variant="labelMedium" style={{ opacity: 0.7 }}>
								{t("redeemCopilot.contactSupport.emailLabel")}
							</Text>
							<Text variant="bodyLarge" style={{ fontWeight: "600" }}>
								{supportEmail}
							</Text>
						</Box>

						<Box style={{ flexDirection: "column", gap: 2 }}>
							<Text variant="labelMedium" style={{ opacity: 0.7 }}>
								{t("redeemCopilot.contactSupport.subjectLabel")}
							</Text>
							<Text
								variant="bodyMedium"
								style={{ fontWeight: "500" }}
								numberOfLines={2}
							>
								{subject}
							</Text>
						</Box>
					</Box>
				</Box>

				<Divider style={{ backgroundColor: theme.colors.surfaceVariant }} />

				<Box style={{ paddingVertical: 16, gap: 8 }}>
					<Text variant="labelMedium" style={{ opacity: 0.7 }}>
						{t("redeemCopilot.contactSupport.messageBodyLabel")}
					</Text>
					<Box
						style={{
							padding: 16,
							borderRadius: theme.roundness,
							minHeight: 80,
							backgroundColor: theme.colors.elevation.level5,
						}}
					>
						<Text variant="bodyMedium" style={{ lineHeight: 20 }}>
							{body}
						</Text>
					</Box>
				</Box>

				<Divider style={{ backgroundColor: theme.colors.surfaceVariant }} />

				<Box style={{ paddingVertical: 16, gap: 8 }}>
					<Text variant="labelMedium" style={{ opacity: 0.7 }}>
						{t("redeemCopilot.contactSupport.appDetailsLabel")}
					</Text>
					<Box
						style={{
							flexDirection: "row",
							flexWrap: "wrap",
							marginHorizontal: -8,
							gap: 16,
						}}
					>
						<Box style={{ paddingHorizontal: 8 }}>
							<Box style={{ gap: 2 }}>
								<Text variant="labelSmall" style={{ opacity: 0.8 }}>
									{t("redeemCopilot.contactSupport.appNameLabel")}
								</Text>
								<Text
									variant="bodyMedium"
									style={{ fontWeight: "500" }}
									numberOfLines={1}
								>
									{appName}
								</Text>
							</Box>
						</Box>

						{Boolean(version) && (
							<Box style={{ paddingHorizontal: 8 }}>
								<Box style={{ gap: 2 }}>
									<Text variant="labelSmall" style={{ opacity: 0.8 }}>
										{t("redeemCopilot.contactSupport.versionLabel")}
									</Text>
									<Text variant="bodyMedium" style={{ fontWeight: "500" }}>
										{version}
									</Text>
								</Box>
							</Box>
						)}

						<Box style={{ paddingHorizontal: 8 }}>
							<Box style={{ gap: 2 }}>
								<Text variant="labelSmall" style={{ opacity: 0.8 }}>
									{t("redeemCopilot.contactSupport.platformLabel")}
								</Text>
								<Text variant="bodyMedium" style={{ fontWeight: "500" }}>
									{platform}
								</Text>
							</Box>
						</Box>
					</Box>
				</Box>
			</Card.Content>
		</Card>
	);
};
